import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ViewEncapsulation,
  inject,
  signal,
} from '@angular/core';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterModule,
} from '@angular/router';

import { ScrollPanelModule } from 'primeng/scrollpanel';

import { NgIconComponent } from '@ng-icons/core';

import { MODULES } from '@configs/modules.enum';

import { IndexableRoute } from '@cxc/core/models';

import { RouterUtilsService, UserStore } from '@cxc/core/services';
import { RouterState } from '@cxc/core/services';
import { NavigationService } from '@services/navigation.service';
import { DashboardState } from '@services/state/dashboard.state';
import { OperationsState } from '@services/state/operation-state/operation.state';

interface SecondaryDrawerRoute {
  title: string;
  fullPath: string;
  path: string;
  icon: string;
  isNew: boolean;
  analyticsLabel?: string;
}

@Component({
  selector: 'secondary-drawer',
  templateUrl: './secondary-drawer.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [RouterModule, CommonModule, NgIconComponent, ScrollPanelModule],

  animations: [
    trigger('expandColapseSecondaryDrawer', [
      state('true', style({ maxWidth: '260px', opacity: 1 })),
      state('false', style({ maxWidth: '0px', opacity: 0 })),
      transition('* => *', animate('0.25s ease-out')),
    ]),
  ],
})
export class SecondaryDrawerComponent {
  parsedRoutes = signal<SecondaryDrawerRoute[]>([
    {
      title: '',
      fullPath: '',
      path: '',
      icon: '',
      isNew: false,
    },
  ]);
  activePath = signal('');

  _parsedRoutes: SecondaryDrawerRoute[] = [];

  routerUtils = inject(RouterUtilsService);

  constructor(
    public locationService: NavigationService,
    public activatedRoute: ActivatedRoute,
    public userStore: UserStore,
    private operationsState: OperationsState,
    public dashboardState: DashboardState,
    private appState: RouterState,

    router: Router
  ) {
    router.events.subscribe(val => {
      if (val instanceof NavigationEnd) {
        this._parsedRoutes = [];
        const urlTree = router.url.split('/');

        // nothing to do
        if (urlTree.length < 2) {
          return;
        }

        this.activePath.set(router.url);

        const iterableRoutes = this.routerUtils.getIterableRouteByModule(
          MODULES.PORTFOLIO
        )!.routes;

        switch (urlTree[1]) {
          case 'dashboard': {
            const dashboard = iterableRoutes['dashboard'];
            const portfolioMonitoring = dashboard.children['portfolio'];
            const facility = portfolioMonitoring.children['facility'];
            const operationId = facility.children[':operationId'];

            this.parseRoute(
              Object.values(operationId.children),
              `/dashboard/portfolio/facility/${this.appState.urlFragments()[3]}/`
            );
            break;
          }
          case 'account': {
            const account = iterableRoutes['account'];

            this.parseRoute(Object.values(account.children), '/account/');
            break;
          }
        }

        this.parsedRoutes.set(this._parsedRoutes);
      }
    });
  }

  parseRoute(children: IndexableRoute[], prePath: string) {
    children.forEach(child => {
      if (child.path != '') {
        this._parsedRoutes.push({
          title: child.title?.toString()!,
          fullPath: prePath + child.path,
          path: child.path!,
          icon: child.data ? child.data!['icon'] : '',
          isNew: child.data ? !!child.data!['isNew'] : false,
          analyticsLabel: child.data?.['analyticsLabel'],
        });
      }
    });
  }
}
